/**
 * PortalPage — `/portal` route. Renders the post-auth Portal Chooser
 * for multi-portal users; redirects everyone else.
 *
 * Per `auth-admin-20260522/AUTH_FLOW.md §3` + `PORTAL_CHOOSER_SPEC.md §7`:
 *
 *   unauthenticated         →  redirect /login
 *   portals.length === 0    →  redirect /no-access (placeholder)
 *   portals.length === 1    →  redirect portalHome(portals[0].id)
 *   portals.length >= 2     →  render <PortalChooser />
 *
 * The page intentionally uses imperative `window.location.assign`
 * for redirects rather than React Router's `<Navigate>` — the
 * `/auth/me` cache is keyed by the session, and a full page nav
 * gives the destination route a clean session-state reset (matches
 * how SCRUM-57's LoginPage hands off to `/` today). When SCRUM-116
 * lands the AdminShell, those routes will live in the same router
 * and we can revisit using `<Navigate>` for an SPA-style hop.
 *
 * `decidePostAuthRoute` is the single source of truth for the
 * branching — same function the route guards on `/pms` / `/msa` /
 * `/admin` will eventually consume (those routes don't ship in
 * SCRUM-113; they're tracked under the SCRUM-15 admin epic).
 *
 * Telemetry placeholder: per `PORTAL_CHOOSER_SPEC.md §9` four events
 * fire on this surface. Until the telemetry pipeline lands as its
 * own ticket, the calls are no-op stubs annotated with the event
 * shape from the spec — easy to wire later, easy to find via grep.
 *
 * `/auth/me` shape tolerance: this page intentionally honors BOTH
 * the current backend's flat DTO (top-level `id` + `email`, no
 * `name`, no `portals[]`) AND the future enriched shape from
 * `AUTH_FLOW.md §2` (nested `user.{name, email, avatar_url}` +
 * top-level `portals[]`). See `readUserShape` JSDoc for the
 * tolerance contract. Until the backend ships `portals[]`, the
 * /portal route reaches the "Loading your portals…" branch and
 * does NOT redirect to /no-access — the null-vs-`[]` distinction
 * in `extractPortalEntitlements` is what keeps that honest.
 *
 * Compact (mobile) mode: when the viewport matches
 * `(max-width: 899px)` per `PORTAL_CHOOSER_SPEC.md §5`, the page
 * passes `compact={true}` to `PortalChooser` to activate the
 * reduced-padding layout. Mirrors the DashboardPage hook pattern
 * at `apps/admin/src/widgets/dashboard/DashboardPage.tsx`.
 */

import * as React from 'react';
import { useSession } from '../../../shared/hooks/useSession.js';
import {
  clearDefaultPortalId,
  readDefaultPortalId,
  writeDefaultPortalId,
} from '../../../shared/auth/default-portal-storage.js';
import { portalHome, type PortalId } from '../../../shared/auth/portal-id.js';
import {
  decidePostAuthRoute,
  extractPortalEntitlements,
} from '../../../shared/auth/use-auth-routing.js';
import { apiClient } from '../../../shared/api-client.js';
import {
  PortalChooser,
  type PortalChooserTile,
  type PortalChooserUser,
} from '../_components/PortalChooser.js';

function navigate(path: string): void {
  if (typeof window === 'undefined') return;
  window.location.assign(path);
}

/** Telemetry event stub. See spec §9 for the full shape. */
function emitTelemetry(event: string, props: Record<string, unknown>): void {
  // No pipeline yet — see file-level JSDoc. Logged at debug level so
  // it's discoverable in browser devtools without leaking to prod
  // analytics until the pipeline lands.
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[telemetry]', event, props);
  }
}

/**
 * Tolerant `/auth/me` shape adapter.
 *
 * Two shapes coexist during the auth-admin-20260522 backend rollout:
 *
 *  1. **Current flat DTO** (shipped today) — `AuthMeResponseDto` at
 *     `apps/api/src/modules/identity/interface/dto/auth-me.response.dto.ts`.
 *     Top-level `id` + `email`; no `name` field, no `user` envelope,
 *     no `portals[]`. The shared FE adapter
 *     `apps/admin/src/shared/hooks/useCurrentUser.ts` explicitly
 *     documents that `name` is intentionally absent today.
 *
 *  2. **Future enriched shape** (per `AUTH_FLOW.md §2`) — nested
 *     `user: { id, name, email, avatar_url? }` + top-level
 *     `portals[]` + `default_portal_id?`. Backend soft-suggestion
 *     ticket; not yet shipped.
 *
 * The adapter prefers the enriched shape when present, falls back to
 * the flat DTO otherwise. Either way the returned name may be `""`,
 * which `PortalChooser.firstName()` resolves to the
 * `"Welcome back, there."` greeting per `COPY.md §i18n notes`. This
 * lets `/portal` behave honestly on the current backend (greeting +
 * eventual chooser render once portals ship) instead of getting
 * stuck behind the future-only shape assumption.
 */
function readUserShape(raw: unknown): PortalChooserUser | null {
  if (raw === null || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  // Future enriched shape: nested `user: { name, email, avatar_url? }`.
  const nested = obj.user;
  if (nested !== null && typeof nested === 'object') {
    const nestedObj = nested as Record<string, unknown>;
    const nName = nestedObj.name;
    const nEmail = nestedObj.email;
    if (typeof nEmail === 'string') {
      const nAvatar = nestedObj.avatar_url;
      return {
        name: typeof nName === 'string' ? nName : '',
        email: nEmail,
        ...(typeof nAvatar === 'string' ? { avatar_url: nAvatar } : {}),
      };
    }
  }

  // Current flat DTO: top-level `id` + `email`, no `name`, no `user`
  // envelope. `PortalChooser.firstName('')` → "there", so the greeting
  // still reads as "Welcome back, there." until the backend ships
  // `name` (or the nested envelope from the future shape).
  const flatEmail = obj.email;
  if (typeof flatEmail === 'string') {
    return { name: '', email: flatEmail };
  }

  return null;
}

const COMPACT_MEDIA_QUERY = '(max-width: 899px)';

/**
 * Defensive `matchMedia` access mirrors the DashboardPage precedent
 * (apps/admin/src/widgets/dashboard/DashboardPage.tsx:93). JSDOM
 * doesn't polyfill `matchMedia`, so this lets the tests run without
 * crashing while real browsers always have the API.
 */
function safeMatchMedia(query: string): MediaQueryList | null {
  if (typeof window === 'undefined') return null;
  if (typeof window.matchMedia !== 'function') return null;
  try {
    return window.matchMedia(query);
  } catch {
    return null;
  }
}

/**
 * `useCompactMode` mirrors DashboardPage's hook of the same name —
 * resolves to `true` for viewports `< 900 px` per the bundle's
 * `PORTAL_CHOOSER_SPEC.md §5 Mobile (< 900 px)` breakpoint. The
 * `override` argument is for tests, which pass an explicit boolean
 * because JSDOM has no real viewport.
 */
function useCompactMode(override?: boolean): boolean {
  const [compact, setCompact] = React.useState<boolean>(() => {
    if (override !== undefined) return override;
    return safeMatchMedia(COMPACT_MEDIA_QUERY)?.matches ?? false;
  });
  React.useEffect(() => {
    if (override !== undefined) {
      setCompact(override);
      return undefined;
    }
    const mq = safeMatchMedia(COMPACT_MEDIA_QUERY);
    if (mq === null) return undefined;
    const onChange = (e: MediaQueryListEvent): void => setCompact(e.matches);
    mq.addEventListener('change', onChange);
    setCompact(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [override]);
  return compact;
}

export interface PortalPageProps {
  /** Override compact mode for tests (otherwise auto-detected). */
  readonly compact?: boolean;
}

export function PortalPage({
  compact: compactOverride,
}: PortalPageProps = {}): React.ReactElement {
  const session = useSession();
  const compact = useCompactMode(compactOverride);

  // Sergey round-2 INFO 2 (2026-05-28): cache the initial localStorage
  // read with `useState` instead of re-reading on every render. The
  // default-portal id only changes via `writeDefaultPortalId` (Continue
  // CTA) or `clearDefaultPortalId` (Sign out) — both of those navigate
  // away, so the cached value is correct for the lifetime of this
  // render branch. MUST live above any early return (Rules of Hooks).
  const [defaultPortalId] = React.useState(readDefaultPortalId);

  // ── Effect 1: auth gate ──────────────────────────────────────────
  React.useEffect(() => {
    if (session.status === 'unauthenticated') {
      navigate('/login');
    }
  }, [session.status]);

  // ── Effect 2: single-portal fast-path ────────────────────────────
  // The redirect for single-portal users fires once `/auth/me`
  // resolves and the host can see `portals[]`. Multi-portal users
  // fall through to the render branch below.
  const portalsForDecision = React.useMemo(() => {
    if (session.status !== 'authenticated') return null;
    return extractPortalEntitlements(session.user);
  }, [session.status, session.user]);

  React.useEffect(() => {
    if (portalsForDecision === null) return; // BE hasn't shipped portals[] yet
    if (portalsForDecision.length >= 2) return; // chooser renders
    const target = decidePostAuthRoute(portalsForDecision);
    navigate(target);
  }, [portalsForDecision]);

  // ── Effect 3: chooser-viewed telemetry ───────────────────────────
  React.useEffect(() => {
    if (portalsForDecision === null) return;
    if (portalsForDecision.length < 2) return;
    emitTelemetry('auth.portal.chooser_viewed', {
      portal_count: portalsForDecision.length,
    });
  }, [portalsForDecision]);

  // ── Render branches ──────────────────────────────────────────────
  if (session.status === 'loading') {
    return (
      <main
        className="flex min-h-screen items-center justify-center"
        style={{ background: 'var(--bg-canvas)', color: 'var(--fg-secondary)' }}
      >
        <p>Loading…</p>
      </main>
    );
  }

  if (session.status === 'unauthenticated') {
    // Effect 1 fires the redirect; this is the in-between paint.
    return <></>;
  }

  if (session.status === 'error') {
    return (
      <main
        className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center p-6 text-center"
        style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
      >
        <h1 className="mb-2 text-xl font-semibold">
          Couldn't load your portals
        </h1>
        <p className="mb-4 text-sm" style={{ color: 'var(--fg-secondary)' }}>
          The session probe failed. Refresh the page, or sign in again.
        </p>
        {/* Tertiary link-styled action — same rationale as the
            PortalChooser sign-out link: kit Button variants paint in
            primary/danger/etc. families, none match the tertiary
            link affordance the error state needs. */}
        {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
        <button
          type="button"
          onClick={() => session.refetch()}
          className="text-sm font-semibold underline-offset-2 hover:underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Retry
        </button>
      </main>
    );
  }

  // authenticated branch ────────────────────────────────────────────
  const user = readUserShape(session.user);
  if (user === null) {
    // /auth/me payload doesn't have the user we expect — most likely
    // pre-bundle BE shape. Fall back to the legacy AuthenticatedRoot
    // landing path; consistent with the SCRUM-73 login redirect.
    return (
      <main
        className="flex min-h-screen items-center justify-center"
        style={{ background: 'var(--bg-canvas)', color: 'var(--fg-secondary)' }}
      >
        <p>Loading…</p>
      </main>
    );
  }

  if (portalsForDecision === null) {
    // BE doesn't yet emit portals[]. Render a loading state — the
    // soft-suggestion backend ticket fills this in. The chooser will
    // resume rendering automatically once the response shape ships
    // (no FE change needed).
    return (
      <main
        className="flex min-h-screen items-center justify-center"
        style={{ background: 'var(--bg-canvas)', color: 'var(--fg-secondary)' }}
      >
        <p>Loading your portals…</p>
      </main>
    );
  }

  if (portalsForDecision.length < 2) {
    // Effect 2 fires the redirect; this is the in-between paint.
    return <></>;
  }

  // Build the tile list with the default-portal "Last used" badge
  // applied to whichever tile matches the localStorage shim.
  // `defaultPortalId` is cached at the top of the component (see Sergey
  // round-2 INFO 2 fix — Rules of Hooks require the read to live
  // above any early return).
  const tiles: PortalChooserTile[] = portalsForDecision.map((entitlement) => {
    const context = entitlement.context ?? {
      // Bundle's `/auth/me` requires `context.primary`; the loose
      // extractor allows the field to be missing for forward-compat,
      // so we fall back to a placeholder rather than crash. Backend
      // soft-suggestion ticket pins this.
      primary: '',
    };
    return {
      id: entitlement.id,
      context,
      ...(defaultPortalId === entitlement.id ? { lastUsed: true } : {}),
    };
  });

  const handleSelect = (id: PortalId): void => {
    writeDefaultPortalId(id);
    emitTelemetry('auth.portal.chosen', { from: 'chooser', portal_id: id });
    // Route to the bundle's canonical portal home. `/pms`, `/msa`,
    // `/admin` aren't registered in ROUTE_MANIFEST yet — SCRUM-198
    // re-introduces a reachability guard (derived from the manifest)
    // once those routes land. Until then this matches round-1 behavior;
    // the dead-end can't fire because /login doesn't route to /portal.
    navigate(portalHome(id));
  };

  const handleSignOut = async (): Promise<void> => {
    emitTelemetry('auth.signed_out', { method: 'different_user_link' });
    // Sergey round-2 BLOCK 2 (2026-05-28): the round-1 implementation
    // fired POST /auth/logout without awaiting, then immediately called
    // `navigate('/login')` which routes through `window.location.assign`.
    // The browser cancels in-flight fetch/XHR on top-level navigation, so
    // on real networks the logout request frequently never leaves the
    // browser. For "Sign in as a different user" on a shared device, that
    // means the prior server-side session stays valid until its natural
    // TTL — a real session-handover risk. Await the response so the
    // server-side revocation actually happens before we unload.
    //
    // The catch is intentionally broad: even if the BE returns 4xx/5xx,
    // we still navigate to /login (the client-side cleanup runs
    // regardless). Best-effort revocation, deterministic navigation.
    try {
      await apiClient.POST('/auth/logout', {});
    } catch {
      /* server-side revocation is best-effort; navigate regardless */
    }
    clearDefaultPortalId();
    navigate('/login');
  };

  return (
    <PortalChooser
      user={user}
      portals={tiles}
      onSelect={handleSelect}
      onSignOut={handleSignOut}
      compact={compact}
    />
  );
}
